import { lazy, Suspense, useRef, useState } from "react";
import BaseDialog from "../../modelDialogs/BaseDialog";
import { itx } from "../../translationItx/itx";
import { CatalogProductPicker, type CatalogProductType, type CatalogProductSubType } from "./CatalogProductPicker";
import type { CatalogProductOption } from "./catalogProductOptions";
import type { CatalogProductSelection } from "./CatalogProductSelection";
import type { BaseDeviceProps } from "../../models/BaseDeviceProps";

const TraditionalCatalogProductPicker = lazy(() => import("./TraditionalCatalogProductPicker"));
const GenericDeviceDialog = lazy(() => import("../../modelDialogs/GenericDeviceDialog"));

/** Shared single-product selection. The caller owns importing/assigning the selected device. */
export default function CatalogProductDialog({ title, productType = "All", subType, genericDefaults, onSelect }: {
  title: string;
  productType?: CatalogProductType;
  subType?: CatalogProductSubType;
  genericDefaults?: BaseDeviceProps;
  onSelect: (product: CatalogProductSelection) => void | Promise<void>;
}) {
  const [selected, setSelected] = useState<CatalogProductSelection>();
  const [family, setFamily] = useState<CatalogProductOption>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const pending = useRef(false);
  const [traditional, setTraditional] = useState(false);
  const [generic, setGeneric] = useState(false);
  const toggleView = () => {
    if (pending.current) return;
    setTraditional(!traditional);
    setSelected(undefined);
    setFamily(undefined);
    setError("");
  };

  const confirm = async (product?: CatalogProductSelection): Promise<boolean> => {
    if (!product || pending.current) return false;
    pending.current = true;
    setBusy(true);
    setError("");
    try {
      await onSelect(product);
      return true;
    } catch (error) {
      setError(error instanceof Error ? error.message : itx("ServerTemplateList.importProductError", "An error occured while importing"));
      return false;
    } finally {
      pending.current = false;
      setBusy(false);
    }
  };

  if (generic) return <Suspense fallback={<span>{itx("productSelection.generic", "Generic")}</span>}>
    <GenericDeviceDialog productType={productType} subType={subType} initialParams={genericDefaults}
      onSelect={onSelect} onBack={() => setGeneric(false)} />
  </Suspense>;

  return (
    <BaseDialog title={title} className={`catalog_addProductDialog${traditional ? " catalog_traditionalDialog" : ""}`} noScroll bodyNoMinWidth
      okCaption={itx("verificationProjectCard.ok", "OK")} okDisabled={!selected || busy}
      onOkAsync={() => confirm(selected)}
      abortCaption={family ? itx("back", "Zurück") : itx("exportGroupAddressesDialog.cancel", "Abbrechen")}
      onAbortAsync={async () => {
        if (pending.current) return false;
        if (!family) return true;
        setFamily(undefined);
        setSelected(undefined);
        setError("");
        return false;
      }}
      onClose={() => !pending.current}>
      {error && <p role="alert">{error}</p>}
      {traditional ? <>
        <div className="catalog_viewToolbar"><button type="button" className="catalog_viewToggle"
          data-testid="catalog-card-view" disabled={busy} onClick={toggleView}
          title={itx("productSelection.cardView", "Kachelansicht")}
          aria-label={itx("productSelection.cardView", "Kachelansicht")}>
          <i className="las la-th-large" aria-hidden="true" />
        </button></div>
        <Suspense fallback={<span>{itx("productSelection.Produkte_werden_geladen", "Produkte werden geladen …")}</span>}>
          <TraditionalCatalogProductPicker productType={productType} onSelectionChange={setSelected} disabled={busy} />
        </Suspense>
      </> : <CatalogProductPicker Type={productType} SubType={subType} selectedProduct={family}
        onGeneric={() => { if (!pending.current) setGeneric(true); }}
        onToggleTraditional={toggleView}
        onProductChange={setFamily} onSelectionChange={setSelected} disabled={busy}
        onProductSelected={async product => {
          if (await confirm(product)) state.dialogManager.closeDialog(true);
        }} />}
    </BaseDialog>
  );
}
